// Google Workspace 監査ラン履歴タブ。SF history.ts のミラー（sampleMode を除いた実データ版 + onRunNow）。
// 一覧（ui/data-table, rowTestId="history-row"）→ 行クリックで loadDetail → ui/drawer に findings を表示。
// onRunNow があれば「今すぐ診断を実行して保存」ボタン（persist 実行 → 再描画）を出す。
import { SEVERITY_LABELS, type Finding, type FindingStatus } from "./audit-data";
import { createDataTable, type Column } from "../ui/data-table";
import { createDrawer } from "../ui/drawer";
import { toFindings, type RunGwAuditResponse, type RunGwAuditFinding } from "./audit-api";
import type { RunSummaryDto, RunDetailDto, RunCountsDto } from "./history-api";
import { showToast } from "../ui/toast";

const FINDING_STATUS_LABEL: Record<FindingStatus, string> = {
  pass: "合格",
  fail: "要対応",
  na: "対象外",
  review: "要確認",
};

// trigger は DTO 上は string（backend は "manual" | "scheduled"）。未知値はそのまま表示。
function triggerLabel(t: string): string {
  if (t === "scheduled") return "スケジュール";
  if (t === "manual") return "手動";
  return t;
}

export interface HistoryDeps {
  // 実ラン履歴（接続済み時に注入・新しい順は backend が保証）。
  readonly runs: readonly RunSummaryDto[];
  // 行クリック時にそのランの詳細(findings 込み)を取得する。
  readonly loadDetail: (runId: string) => Promise<RunDetailDto>;
  // 「今すぐ診断を実行して保存」ボタン。persist 実行 → 一覧再描画を呼び出し側に委譲する。
  readonly onRunNow?: () => Promise<void>;
}

function fmtDateTime(iso: string): string {
  return iso.slice(0, 16).replace("T", " ");
}

function fmtCounts(c: RunCountsDto): string {
  return c.critical + "/" + c.high + "/" + c.medium + "/" + c.low;
}

function detailLoading(): HTMLElement {
  const p = document.createElement("p");
  p.dataset["testid"] = "history-detail-loading";
  p.className = "py-6 text-sm text-slate-500";
  p.textContent = "読み込み中...";
  return p;
}

function detailError(): HTMLElement {
  const p = document.createElement("p");
  p.dataset["testid"] = "history-detail-error";
  p.className = "py-6 text-sm text-rose-600";
  p.textContent = "詳細の取得に失敗しました。時間をおいて再度お試しください。";
  return p;
}

// ラン詳細(findings)を drawer の中身としてレンダリングする。
function buildDetailContent(d: RunDetailDto): HTMLElement {
  const resp: RunGwAuditResponse = {
    healthCheckScore: d.healthCheckScore,
    findings: d.findings.map(
      (f): RunGwAuditFinding => ({
        checkId: f.checkId,
        status: f.status as RunGwAuditFinding["status"],
        affectedCount: f.affectedCount,
        affectedResources: f.affectedResources,
      }),
    ),
    coverage: d.coverage,
    runAt: d.runAt,
    domain: d.domain,
  };
  const { findings, notEvaluated } = toFindings(resp);

  const wrap = document.createElement("div");
  wrap.className = "space-y-3";

  const meta = document.createElement("p");
  meta.className = "text-sm text-slate-600";
  meta.textContent =
    "スコア " + d.healthCheckScore + "% ・ 検出(重大/高/中/低) " + fmtCounts(d.counts);
  wrap.append(meta);

  const columns: Column<Finding>[] = [
    { key: "severity", header: "重大度", cell: (f) => SEVERITY_LABELS[f.check.severity] },
    { key: "title", header: "チェック", cell: (f) => f.check.title },
    { key: "status", header: "状態", cell: (f) => FINDING_STATUS_LABEL[f.status] },
    { key: "affected", header: "影響数", cell: (f) => String(f.affectedCount) },
  ];
  wrap.append(
    createDataTable<Finding>({
      caption: "このランの検出一覧",
      columns,
      rows: findings,
      emptyMessage: "検出はありません。",
      rowTestId: "history-detail-row",
    }),
  );

  if (notEvaluated.length > 0) {
    const ne = document.createElement("p");
    ne.className = "text-sm text-slate-500";
    ne.dataset["testid"] = "history-detail-not-evaluated";
    ne.textContent = "未評価 " + notEvaluated.length + " 件（本診断の対象外）";
    wrap.append(ne);
  }
  return wrap;
}

// 「今すぐ診断を実行して保存」ボタン。クリック → onRunNow（persist 実行 + 再描画）。
function renderRunNowBar(onRunNow: () => Promise<void>): HTMLElement {
  const bar = document.createElement("div");
  bar.className = "mb-4 flex justify-end";
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className =
    "rounded bg-shinonome-700 px-4 py-2 text-sm font-medium text-white hover:bg-shinonome-800 disabled:opacity-60";
  btn.textContent = "今すぐ診断を実行して保存";
  btn.addEventListener("click", () => {
    btn.disabled = true;
    void (async () => {
      try {
        await onRunNow();
      } catch {
        showToast({
          tone: "warning",
          title: "診断の実行に失敗しました",
          message: "時間をおいて再度お試しください。",
        });
        btn.disabled = false;
      }
    })();
  });
  bar.append(btn);
  return bar;
}

export function renderGwHistory(root: HTMLElement, deps: HistoryDeps): void {
  root.replaceChildren();

  if (deps.onRunNow !== undefined) {
    root.append(renderRunNowBar(deps.onRunNow));
  }

  if (deps.runs.length === 0) {
    const empty = document.createElement("p");
    empty.dataset["testid"] = "history-empty";
    empty.className = "py-6 text-sm text-slate-500";
    empty.textContent =
      "まだ診断履歴がありません。「今すぐ診断を実行して保存」で診断を実行してください。";
    root.append(empty);
    return;
  }

  const drawer = createDrawer({ title: "ラン詳細" });

  async function openDetail(run: RunSummaryDto): Promise<void> {
    drawer.setTitle(fmtDateTime(run.runAt) + " の診断結果");
    drawer.setContent(detailLoading());
    drawer.open();
    try {
      const detail = await deps.loadDetail(run.runId);
      drawer.setContent(buildDetailContent(detail));
    } catch {
      drawer.setContent(detailError());
      showToast({
        tone: "warning",
        title: "詳細の取得に失敗しました",
        message: "時間をおいて再度お試しください。",
      });
    }
  }

  const columns: Column<RunSummaryDto>[] = [
    { key: "runAt", header: "日時", cell: (r) => fmtDateTime(r.runAt) },
    { key: "score", header: "スコア", cell: (r) => r.healthCheckScore + "%" },
    { key: "counts", header: "検出(重大/高/中/低)", cell: (r) => fmtCounts(r.counts) },
    { key: "trigger", header: "種別", cell: (r) => triggerLabel(r.trigger) },
    { key: "actorSub", header: "実行者", cell: (r) => r.actorSub },
  ];

  root.append(
    createDataTable<RunSummaryDto>({
      caption: "監査の実行履歴",
      columns,
      rows: deps.runs,
      onRowClick: (run) => void openDetail(run),
      rowTestId: "history-row",
    }),
    drawer.element,
  );
}
